/**
 * The editor bar's file actions: export the programme as JSON, import one to
 * replace it, or reset it to an empty draft.
 *
 * @module components/editor/ProgrammeFileActions
 */

import React, { useCallback, useMemo, useRef } from "react";

import { useCanOrPermissive } from "@/components/context/PermissionContext";
import { type MenuItem, useConfirm, useToast } from "@/components/ui";
import { downloadJson, importProgrammeFromJson } from "@/export/json";
import { useEditSession } from "@/platform/editor-sync-context";
import {
  defaultProgramme,
  importedProgramme,
  replacingProgramme,
  resetEditorNavigation,
} from "@/state/store";
import type { Programme } from "@/types";

export interface ProgrammeFileMenu {
  /** Menu entries for the actions this account may take. */
  items: readonly MenuItem[];
  /** The hidden file input Import opens; render it beside the menu. */
  fileInput: React.ReactElement | null;
}

/**
 * Export, import and reset as menu entries, each gated on what the account
 * may do. Import and reset replace the content, so a closed programme has
 * neither.
 */
export function useProgrammeFileActions(
  programme: Programme,
  readOnly: boolean,
): ProgrammeFileMenu {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const confirm = useConfirm();
  const toast = useToast();
  const resource = { type: "programme", programmeId: programme.id } as const;
  const canRead = useCanOrPermissive("programme:read", resource);
  const canWrite = useCanOrPermissive("programme:write", resource);
  const replaces = canWrite && !readOnly;
  const { edit } = useEditSession();

  const handleExport = useCallback(() => {
    downloadJson(programme);
  }, [programme]);

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      const result = await importProgrammeFromJson(file);
      if (!result.success || !result.programme) {
        toast({
          message: `Import failed: ${result.error || "Unknown error"}`,
          tone: "warning",
          "data-testid": "programme-import-failed",
        });
      } else if (
        // Asked once the file has read, so a broken file is refused without
        // a question, and before anything is replaced.
        await confirm({
          title: "Replace this programme?",
          body: `Everything in this programme is replaced with the contents of ${file.name}.`,
          confirmLabel: "Replace programme",
          tone: "danger",
          "data-testid": "programme-import-confirmation",
        })
      ) {
        // One edit, sent at once: the whole document changes in one step.
        edit(replacingProgramme(importedProgramme(result.programme)), { save: "now" });
        // Trigger global re-render for any legacy components
        const win = window as Window & { render?: () => void | Promise<void> };
        win.render?.();
      }

      // Reset file input
      event.target.value = "";
    },
    [confirm, edit, toast],
  );

  const handleReset = useCallback(() => {
    confirm({
      title: "Reset this programme?",
      body: "Everything in this programme is deleted and it goes back to an empty draft.",
      confirmLabel: "Reset programme",
      tone: "danger",
      "data-testid": "programme-reset-confirmation",
    })
      .then((confirmed) => {
        if (!confirmed) {
          return;
        }
        edit(replacingProgramme(defaultProgramme()), { save: "now" });
        resetEditorNavigation();
        // Legacy components still render from a global hook.
        const win = window as Window & { render?: () => void | Promise<void> };
        win.render?.();
      })
      .catch(() => {
        // The provider settles rather than rejects; nothing to recover.
      });
  }, [confirm, edit]);

  const items = useMemo<MenuItem[]>(
    () => [
      ...(canRead
        ? [
            {
              key: "backup",
              label: "JSON backup",
              icon: "download-simple",
              onSelect: handleExport,
              "data-testid": "export-btn",
            },
          ]
        : []),
      ...(replaces
        ? [
            {
              key: "import",
              label: "Import",
              icon: "upload-simple",
              onSelect: () => fileInputRef.current?.click(),
              "data-testid": "import-btn",
            },
            {
              key: "reset",
              label: "Reset",
              icon: "arrow-counter-clockwise",
              tone: "danger" as const,
              separated: true,
              onSelect: handleReset,
              "data-testid": "reset-btn",
            },
          ]
        : []),
    ],
    [canRead, replaces, handleExport, handleReset],
  );

  // The input stays hidden and carries the file itself; the menu item opens
  // it, so a keyboard reaches Import through the menu.
  const fileInput = replaces ? (
    <input
      ref={fileInputRef}
      type="file"
      accept=".json,application/json"
      onChange={handleFileChange}
      hidden
      data-testid="import-input"
      aria-label="Import programme from JSON file"
    />
  ) : null;

  return { items, fileInput };
}
